import { useRef, type KeyboardEvent } from "react";
import { Frame, type BBox } from "./Frame.js";
import { mmss } from "../lib/demo.js";

export interface Candidate {
  timecodeMs: number;
  /** quality note shown in the caption (e.g. "くっきり ◎") */
  label?: string;
  recommended?: boolean;
  imageUrl?: string | null;
  bbox?: BBox | null;
}

interface CandidatePickerProps {
  candidates: Candidate[];
  /** index of the selected candidate */
  selected: number;
  onSelect: (index: number) => void;
  ariaLabel?: string;
}

/**
 * Multi-candidate keyframe picker — the product's differentiator (screen G).
 * WAI-ARIA radiogroup with roving tabindex + arrow-key navigation, matching ui/assets/ui.js.
 */
export function CandidatePicker({
  candidates,
  selected,
  onSelect,
  ariaLabel = "代表フレームの候補",
}: CandidatePickerProps) {
  const refs = useRef<(HTMLElement | null)[]>([]);

  function onKeyDown(e: KeyboardEvent<HTMLElement>, i: number) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(i);
      return;
    }
    let next = i;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % candidates.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i - 1 + candidates.length) % candidates.length;
    else return;
    e.preventDefault();
    refs.current[next]?.focus();
    onSelect(next);
  }

  // The prototype's .cands3 always shows three frames per step. Keep that layout even when the
  // AI returns fewer candidates by padding with non-interactive placeholder cells.
  const placeholders = Math.max(0, 3 - candidates.length);

  return (
    <div className="cands3" role="radiogroup" aria-label={ariaLabel}>
      {candidates.map((c, i) => {
        const on = i === selected;
        const tc = mmss(c.timecodeMs);
        return (
          <figure
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            className="cand"
            role="radio"
            aria-checked={on}
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onSelect(i)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {c.recommended ? <span className="star">★ AI</span> : null}
            <Frame src={c.imageUrl} cap={tc} bbox={c.bbox ?? null} />
            <figcaption>
              <span>{tc}</span>
              {c.label ? <span>{c.label}</span> : null}
            </figcaption>
          </figure>
        );
      })}
      {Array.from({ length: placeholders }, (_, p) => (
        <figure key={`ph-${p}`} className="cand" aria-hidden="true" style={{ opacity: 0.45, pointerEvents: "none" }}>
          <Frame subject={false} />
          <figcaption>
            <span>—</span>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}
